/**
 * Legacy-side locale-aware path resolver for Next.js embed routes.
 *
 * Promoted from the duplicated `resolveNextViewerPath` /
 * `resolveNextComposerPath` helpers that previously lived inside
 * `UserStoriesPage.tsx` and `StoryComposePage.tsx`. Used by
 * `<LegacySurfaceBridge mode="iframe">` so every bridged surface
 * resolves its locale prefix the same way.
 *
 * Locale convention (matches frontend-next `routing.ts`): `as-needed`
 * prefixes; `en-US` has no prefix, `ja-JP` → `/ja`, `ko-KR` → `/ko`.
 */

import { resolveStoryLocale } from "./storyUi";
import type { SurfaceKind } from "types/surfaces";
import { SURFACE_REGISTRY } from "types/surfaces";

export type BridgeLocale = "en-US" | "ja-JP" | "ko-KR";

export interface ResolveEmbedPathInput {
  /** Which registered surface to embed. */
  surfaceKind: SurfaceKind;
  /** Legacy entity type (e.g., `"issue"`, `"post"`, `"user"`). */
  entityType: string;
  /** Legacy entity id. Stringified via `encodeURIComponent`. */
  entityId: string | number;
  /**
   * Optional override. When omitted, falls back to `resolveStoryLocale`
   * which reads `navigator.language` with a `"en-US"` default. Callers
   * that already know the user's locale (e.g., from the CRA i18n
   * provider) should pass it explicitly for consistency.
   */
  locale?: BridgeLocale;
  /**
   * Extra query params appended to the resolved path. Callers never
   * need to add `embed=1` manually; `withEmbed` controls that.
   */
  query?: Record<string, string | number | undefined>;
  /**
   * Whether to append `?embed=1`. Defaults to `true` because the bridge
   * always consumes the Next surface in embed mode. Set to `false` only
   * for tests or debug links.
   */
  withEmbed?: boolean;
}

/** Map `BridgeLocale` to the URL prefix Next uses (`as-needed`). */
const LOCALE_URL_PREFIX: Record<BridgeLocale, string> = {
  "en-US": "",
  "ja-JP": "/ja",
  "ko-KR": "/ko",
};

const substituteEntityPlaceholders = (
  template: string,
  entityType: string,
  entityId: string | number
): string =>
  template
    .replace(":entityType", encodeURIComponent(entityType))
    .replace(":entityId", encodeURIComponent(String(entityId)));

const buildQuery = (
  query: Record<string, string | number | undefined> | undefined,
  withEmbed: boolean
): string => {
  const params = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      params.set(key, String(value));
    }
  }
  if (withEmbed) {
    params.set("embed", "1");
  }
  const serialized = params.toString();
  return serialized.length > 0 ? `?${serialized}` : "";
};

/**
 * Resolve the same-origin Next embed path for a given surface.
 *
 * Returned path is relative to the current origin so the iframe
 * stays on the legacy host (cookies + postMessage work).
 */
export const resolveNextEmbedPath = ({
  surfaceKind,
  entityType,
  entityId,
  locale,
  query,
  withEmbed = true,
}: ResolveEmbedPathInput): string => {
  const entry = SURFACE_REGISTRY[surfaceKind];
  if (!entry) {
    throw new Error(`Unknown surfaceKind "${surfaceKind}"`);
  }

  const effectiveLocale: BridgeLocale = locale ?? resolveStoryLocale();
  const prefix = LOCALE_URL_PREFIX[effectiveLocale];
  const body = substituteEntityPlaceholders(
    entry.embedPathTemplate,
    entityType,
    entityId
  );
  const search = buildQuery(query, withEmbed);
  return `${prefix}${body}${search}`;
};
